import Hls, {
  CapLevelController,
  type ErrorData,
  type HlsConfig,
  type LevelLoadedData,
} from 'hls.js';
import { emeAvailable, reuseMediaKeysFor } from '../drm/cdm';
import { classifyDrmStatus, PlaybackError } from '../errors';
import { nativeHlsSupported, webkitMediaStack } from './native';
import {
  hostOf,
  readFrameStats,
  type AudioTrackInfo,
  type PlaybackStats,
  type QualityLevel,
  type TextTrackInfo,
} from '../stats';
import { type EngineDeps, type PlaybackEngine } from './types';

export function hlsSupported(): boolean {
  return Hls.isSupported();
}

const MAX_MEDIA_RECOVERIES = 3;
const MAX_NETWORK_RECOVERIES = 5;
// hls.js has no license-request retry policy (there's certLoadPolicy/keyLoadPolicy
// but no licenseLoadPolicy), and KEY_SYSTEM_LICENSE_REQUEST_FAILED is fatal by
// default. A transient transport failure of the license POST is recoverable by
// rebuilding the instance (fresh key session → fresh license request).
const MAX_LICENSE_RECOVERIES = 3;
const RECOVERY_RESET_MS = 30_000;
// End-of-stream watchdog (live only). The live playlist not advancing for this
// long — with no #EXT-X-ENDLIST — means the stream ended: Tencent's origin
// freezes the manifest at end (HTTP 200, media sequence static) instead of
// publishing ENDLIST, so hls.js would otherwise buffer forever. 12s is safely
// above the worst observed edge refresh hiccup (~8s).
const END_STALE_MS = 12_000;
const END_WATCH_INTERVAL_MS = 3_000;
// Stale-playlist chase suspension. A frozen live manifest keeps hls.js's
// LatencyController extrapolating the live edge with wall clock, so measured
// latency grows 1s/s; once it crosses liveMaxLatencyDuration — which happens
// ceiling−target ≈ 9-10s after the freeze with our defaults, BEFORE the 12s
// watchdog concludes — the controller force-seeks into data that doesn't
// exist and the viewer sees a repeat-frame/reset loop until 'ended' lands.
// The server can't cover this window: the SSE ended push may trail the
// freeze by up to the bridge's 120s ingest-teardown debounce (feed-dies-
// first path), so the client suspends chasing itself once the playlist has
// been stale this long, and resumes the moment it advances again (a genuine
// origin hiccup keeps full drift protection afterwards). Must stay below
// ceiling−target (10s at the default target, 9s at liveLatencyTarget 3) and
// above the 3s watch tick + normal playlist cadence.
const CHASE_SUSPEND_STALE_MS = 6_000;
// Pause suspension. hls.js keeps chasing the live edge while the element is
// PAUSED: synchronizeToLiveEdge runs on every playlist reload with no
// `media.paused` check, so once the frozen playhead falls liveMaxLatencyDuration
// (12s here) behind the edge it is reset to the live-sync position and fresh
// segments load from there — forever. A paused 1080p viewer pulled the stream
// at live rate (80–105 MB/min, latency sawing 4.5→12s, portal session
// 2026-09-19). The pre-play case was already closed by deferLoad (AV-202); this
// closes the post-play one: after this grace the loader is stopped, and the
// next play() restarts it at the paused position (hls.js startLoad(-1) resumes
// at lastCurrentTime; its next playlist reload re-syncs to live if far behind —
// the same seek as before, now only when the viewer asked to play). A pause
// shorter than the grace resumes from the untouched buffer. Must stay below
// ceiling−target (10s) so the loader is quiet before the first forced re-sync.
const PAUSE_SUSPEND_MS = 5_000;
// Video-starvation watchdog (live only). Kept as defence in depth: the wedge
// below is fixed in the pinned 1.7.2 (upstream #7874 moved key loading behind
// the part/hint selection, and we backported it to the 1.6.x line as #7976),
// but the detection is generic — it catches any silently-dropped video load,
// not only this mechanism, and it is the only thing that noticed this one.
// The original wedge, on hls.js 1.6.x and encrypted LL-HLS: when a part of the
// forward-looking `fragmentHint` fragment is selected while that
// fragment's key-loading promise is in flight, `_doFragLoad` reassigns
// `fragCurrent` to the hint fragment, the key promise's context check then
// sees a "changed" fragment and resolves null, and the load is silently
// dropped with the state machine parked in FRAG_LOADING (a sibling path parks
// it in KEY_LOADING). No loader is in flight, so no load-policy timeout ever
// fires: the level playlist keeps polling, audio keeps playing, and no video
// fragment is ever requested again. Reproduced 2026-08-12 against feed-dev
// with the fullscreen/PiP resize storm from Josef's report — the resize makes
// `capLevelToPlayerSize` flap levels, which corrupts targetBufferTime past the
// playlist's fragment end and steers loading onto the hint fragment.
// Detection: the video element starved (readyState < HAVE_FUTURE_DATA) while
// the live playlist still advances and hls.js has issued no video fragment
// load for a while — that combination is never a plain rebuffer (rebuffering
// keeps FRAG_LOADING events coming) and never stream end (the playlist would
// be stale, which the end watchdog owns). Recovery: stopLoad() + startLoad(-1)
// rebuilds the stream-controller state machine at the live edge; media stays
// attached so EME key sessions survive and playback resumes in ~1-2s.
const STARVATION_FRAG_QUIET_MS = 4_000;
/** Consecutive starved watchdog ticks (3s apart) before kicking. */
const STARVATION_TICKS = 2;
const MAX_STARVATION_KICKS = 5;
// Escalation, for the wedges the kick provably cannot clear. A kick keeps the
// media element attached — deliberately, so EME key sessions survive — but that
// is exactly why it fails against a wedge held in hls.js's KEY-loader cache: a
// `keyInfo.keyLoadPromise` that never settles is only discarded by detach() or
// destroy(), so stopLoad()/startLoad() re-await the same dead promise and park
// again within a second. Observed 2026-09-03 at join, when a client clock
// running behind the licence server produced a first key status of `expired`:
// hls.js renewed the key session and orphaned the promise the fragment loads
// were waiting on, and all five kicks re-parked (the viewer's own recovery was
// a page reload, which destroys the instance). Fixed at the source in
// havik-drm (licence start_datetime is back-dated) and structurally in hls.js
// 1.7.x (the EME path no longer caches that promise) — this is the client-side
// net for the next key-status hiccup, and for whatever else the kick cannot
// reach: if the pipeline is still starved this long after a kick, the instance
// is unrecoverable in place and gets rebuilt, which is what a reload did.
// Grace after a kick for main-track media to buffer before rebuilding, counted
// in watchdog ticks rather than wall-clock milliseconds. Ticks only accrue
// while the tab is visible and the wedge signature still holds, so the grace
// measures ACTIVE starvation: a tab backgrounded right after a kick cannot burn
// it through throttled or suspended timers and come back eligible for an
// immediate rebuild. Same reason STARVATION_TICKS is a tick count.
const STARVATION_KICK_GRACE_TICKS = 2;
/** Rebuilds per healthy-playback window. Two, because a rebuild that doesn't
 *  take won't start taking: a fresh instance re-requests the licence, so the
 *  second attempt covers a transient failure and further ones just churn. */
const MAX_STARVATION_REBUILDS = 2;
// SDK-managed player-size capping (replaces hls.js's `capLevelToPlayerSize`
// poller). hls.js re-reads the player's pixel box every 1s and applies a new
// cap IMMEDIATELY, so a fullscreen enter/exit — which animates through
// intermediate sizes for a few hundred ms — produces 1-2 ABR switches per
// transition. At the LL live-sync target the forward buffer IS ~2s, and each
// switch costs a cold rendition-playlist fetch at the CDN edge plus a decoder
// re-init (slowest through Windows hardware-DRM pipelines), so every switch is
// a visible ~1s stall right after the transition (the fullscreen-exit stutter
// follow-up to Josef's freeze report: 1-2 stalls per exit, worst on Edge).
// The SDK owns the cap instead: identical level selection — hls.js's own
// CapLevelController.getMaxLevelByMediaSize on the DPR-scaled element box —
// but a change only applies once the box has been STABLE for a settle window,
// so mid-animation intermediate sizes never reach ABR. Raises settle briefly:
// entering fullscreen should still upgrade quickly. (Known trade vs upstream,
// VOD only: upstream flushed the forward buffer on a cap raise, so deep-buffer
// VOD upgraded within ~a fragment; here the raise shows once buffered media
// plays out. On live-LL the flush was a no-op anyway — hls.js skips it below
// 2x target duration of buffer, which our latency target guarantees.)
const CAP_RAISE_SETTLE_MS = 2_000;
// A shrink needs no switch to look right — the higher rendition simply
// downscales into the smaller box; a down-cap only saves bandwidth. Applied
// lazily, so a fullscreen exit never pays a switch stall at the transition and
// a re-enter inside the window cancels the pending down-cap outright.
const CAP_SHRINK_LAZY_MS = 30_000;

/**
 * Default live-edge target (seconds behind the edge) when the caller sets none.
 *
 * 2s, not the bleeding `PART-HOLD-BACK` edge (~1.5s = 3x part duration), so the
 * playhead parks where Tencent's edge has already settled the parts — chasing
 * hold-back on a low-traffic stream hits the cold per-object origin-pull flap
 * (parts/.m3u8 404<->200) and rebuffers continuously.
 *
 * Was 3s. Lowered after the operator Demo Player (havik-ops
 * streams-player.component.ts) ran 2s from 2026-07-22 to 2026-08-03 with no
 * regression, on srt-bridge rc.34+ (encoder-latency pins) where the cold-edge
 * part flap no longer reproduces. 2s keeps ~0.35s of settle margin over
 * hold-back instead of ~1.35s. If low-traffic streams start rebuffering again,
 * that view is the canary — restore 3 there first.
 */
const LIVE_SYNC_DURATION_S = 2;
/** Recovery band above the target; the drift ceiling is target + this (min 12s). */
const LIVE_LATENCY_MARGIN_S = 9;
/**
 * How far past the live-sync position a seek may land before it's clamped back.
 * Slack so hls.js's own forward nudges — the ≤1.5x catch-up overshooting by a
 * frame, a maxBufferHole gap-skip — never trip the clamp; a scrub to the end of
 * the DVR bar overshoots by seconds, not tenths.
 */
const OVER_SEEK_TOLERANCE_S = 0.5;
/** Recent segment download durations kept for the beacon layer's per-interval
 *  percentiles. One heartbeat spans ~7 segments at a 2s target; 64 leaves room
 *  for LL-HLS part-level traffic without unbounded growth. */
const MAX_REQUEST_SAMPLES = 64;

/**
 * Resolve the live-edge target (`liveSyncDuration`) + drift ceiling
 * (`liveMaxLatencyDuration`) on the final, caller-merged config, keeping it legal
 * for hls.js — mutates `config` in place.
 *
 * Why a target + ceiling at all: `lowLatencyMode` otherwise pins playback to
 * `PART-HOLD-BACK` (the bleeding edge), where Tencent's newest parts aren't
 * reliably published yet → constant rebuffering; and after a stall, latency
 * drifts unbounded toward the DVR window (~90s seen in the field). The target
 * parks ~2s back where parts are settled; the ceiling force-seeks back once
 * latency exceeds it, capping the drift. (`maxLiveSyncPlaybackRate` recovers
 * smoothly inside the band first.) Live/LL only — ignored on VOD.
 *
 * Why normalize rather than hard-code: hls.js THROWS at construction if the
 * Count and Duration variants are mixed, or if `liveMaxLatencyDuration <=
 * liveSyncDuration`. The `hlsConfig` escape hatch lets a caller pass either, so:
 *  - caller opted into the Count variants → defer entirely (never inject seconds);
 *  - otherwise default the target to 3 and keep the ceiling strictly above it,
 *    repairing a one-sided or illegal Duration override instead of throwing.
 */
function normalizeLiveSync(config: Partial<HlsConfig>): void {
  if (config.liveSyncDurationCount != null || config.liveMaxLatencyDurationCount != null) {
    // Caller chose count-based live sync — leave it; never mix in seconds keys.
    delete config.liveSyncDuration;
    delete config.liveMaxLatencyDuration;
    return;
  }
  if (config.liveSyncDuration == null) config.liveSyncDuration = LIVE_SYNC_DURATION_S;
  const ceiling = Math.max(12, config.liveSyncDuration + LIVE_LATENCY_MARGIN_S);
  if (
    config.liveMaxLatencyDuration == null ||
    config.liveMaxLatencyDuration <= config.liveSyncDuration
  ) {
    config.liveMaxLatencyDuration = ceiling;
  }
  deriveAbrReaction(config, config.liveSyncDuration);
}

/**
 * Scale ABR's DOWN-switch reaction to the live buffer we actually have.
 *
 * hls.js ships these sized for a multi-second forward buffer:
 * `maxStarvationDelay` 4 and `maxLoadingDelay` 4 (the starvation budget alone
 * is larger than our whole buffer, so ABR's own accounting still reads "fine"
 * while the media element is already stalling) and `abrEwmaFastLive` 3 (a
 * bandwidth drop takes 3s+ to move the estimate at all). On a live stream the
 * forward buffer IS `liveSyncDuration`, so at our target there is no runway:
 * a viewer whose link degrades rebuffers at the top rung for seconds instead
 * of dropping a rung. (Reported on the ops demo player, same config family:
 * throttled link → 1080p buffering → manual 720p fixed it.)
 *
 * LL-HLS also blunts hls.js's other rescue — `_abandonRulesCheck` aborts an
 * in-flight load and drops a rung, but it projects the overage over the
 * current PART (sub-second of media), so the overage looks small and the abort
 * rarely fires. These budgets are therefore the primary defence.
 *
 * Derived from the resolved target rather than hard-coded so responsiveness
 * stays consistent if the target moves (the 3s → 2s rollout). Caller values
 * always win — this only fills gaps. Skipped for count-based live sync, where
 * the target isn't expressible in seconds (documented escape hatch).
 */
function deriveAbrReaction(config: Partial<HlsConfig>, syncS: number): void {
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  // Half the buffer: ABR must decide while there is still media to play.
  if (config.maxStarvationDelay == null) {
    config.maxStarvationDelay = clamp(syncS / 2, 0.5, 4);
  }
  // A fragment load that eats the whole buffer is already too slow.
  if (config.maxLoadingDelay == null) {
    config.maxLoadingDelay = clamp(syncS, 1, 4);
  }
  // The estimate has to move inside the runway, not after it.
  if (config.abrEwmaFastLive == null) {
    config.abrEwmaFastLive = clamp(syncS / 2, 1, 3);
  }
}

/** hls.js adapter — LL-HLS + Widevine/FairPlay EME, ported from the proven Demo Player config. */
export class HlsEngine implements PlaybackEngine {
  readonly name = 'hls' as const;
  private hls: Hls | null = null;
  private readonly deps: EngineDeps;
  /** Current signed Widevine license URL; mutable so it can be refreshed mid-session. */
  private licenseUrl: string | undefined;
  /** FairPlay application-certificate URL (public but auth'd — unsigned, stable). */
  /** Whether the live media playlist actually carries LL-HLS tags (detected, not requested). */
  private detectedLowLatency = false;
  // Consumption counters for the analytics heartbeat (plan A2/§3.1): the
  // beacon layer samples these cumulative totals via getStats() and computes
  // per-interval deltas itself, so the engine never needs a beacon cadence.
  private bytesLoaded = 0;
  private playlistBytesLoaded = 0;
  private renditionSwitches = 0;
  private licenseTimeMs = 0;
  /** A licence request has actually been issued. The descriptor flag only
   *  says DRM was CONFIGURED; a session that dies before EME does anything
   *  should not report a key system it never negotiated. */
  private licenseRequested = false;
  // Contract v2 counters. Switch DIRECTION splits renditionSwitches (which
  // stays the total): a session that only ever shifts down is a starved
  // viewer, one that oscillates is an unstable estimate, and the two are
  // indistinguishable from the total alone.
  private upshifts = 0;
  private downshifts = 0;
  private lastLevelIndex = -1;
  // Request timing and outcome. requestTimesMs is a ring of the most recent
  // durations; the beacon layer takes the tail its interval added.
  private requestCount = 0;
  private requestErrors = 0;
  private readonly requestTimesMs: number[] = [];
  private cdnHost = '';
  // The offered ladder, cached at MANIFEST_PARSED — recomputing it on every
  // 2s stats poll would walk every level for a value that cannot change.
  private ladderHeights: number[] | undefined;
  private ladderTopBitrateKbps = 0;
  // Recovery budgets are windowed: they reset after a stretch of healthy playback
  // (FRAG_BUFFERED), so a long live session isn't permanently disabled by a few
  // unrelated transient glitches spread across hours.
  private mediaRecoveries = 0;
  private networkRecoveries = 0;
  private licenseRecoveries = 0;
  private recoveryResetTimer: ReturnType<typeof setTimeout> | null = null;
  private networkRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private licenseRetryTimer: ReturnType<typeof setTimeout> | null = null;
  /** visibilitychange handler — snaps back to the live edge after a backgrounded tab. */
  private onVisibility: (() => void) | null = null;
  /** seeking handler — clamps a seek past the live-sync position back to it. */
  private onSeeking: (() => void) | null = null;
  // Pause suspension state (see PAUSE_SUSPEND_MS).
  private onPause: (() => void) | null = null;
  private onPlay: (() => void) | null = null;
  private pauseSuspendTimer: ReturnType<typeof setTimeout> | null = null;
  /** Loading is stopped for a lingering pause; the next play() restarts it. */
  private loadSuspended = false;
  /** stop() was called (end of stream): a later `play` event must not restart loading. */
  private stopped = false;
  // End-of-stream watchdog state (armed once the stream is seen LIVE).
  private sawLive = false;
  private lastEndSN = -1;
  private lastAdvanceMs = 0;
  private endWatchdog: ReturnType<typeof setInterval> | null = null;
  private endedEmitted = false;
  // Video-starvation watchdog state (see STARVATION_* constants).
  private lastFragLoadingMs = 0;
  private starvedTicks = 0;
  private starvationKicks = 0;
  /** A kick is awaiting proof it worked; cleared by main-track FRAG_BUFFERED. */
  private kickPending = false;
  /** Active starved ticks observed since that kick (see STARVATION_KICK_GRACE_TICKS). */
  private kickTicks = 0;
  private starvationRebuilds = 0;
  // Stale-playlist chase suspension (see CHASE_SUSPEND_STALE_MS).
  private chaseSuspended = false;
  private savedLiveMaxLatency: number | undefined;
  private savedMaxSyncRate: number | undefined;
  // SDK-managed player-size cap state (see the CAP_* constants).
  private capObserver: ResizeObserver | null = null;
  private capTimer: ReturnType<typeof setTimeout> | null = null;
  /** Size-derived cap currently applied (level index); null = none applied yet. */
  private sizeCapLevel: number | null = null;
  /** Cap derived from setMaxBitrate (-1 = none); combined with the size cap via min. */
  private bitrateCapLevel = -1;
  /** False when the caller opted back into hls.js's own capLevelToPlayerSize poller. */
  private sizeCapManaged = true;
  // DPR watcher: ResizeObserver only tracks the CSS box, so a devicePixelRatio
  // change with an unchanged layout (window dragged to a different-DPI
  // monitor, OS scale change) would never re-evaluate the cap. The media query
  // pins the CURRENT ratio, so any change fires once; the handler re-pins.
  private dprMedia: MediaQueryList | null = null;
  private onDprChange: (() => void) | null = null;

  /** False while a deferLoad engine waits for startLoad(); guards the config
   *  key on a license-retry rebuild and makes startLoad() idempotent. */
  private loadStarted: boolean;

  constructor(deps: EngineDeps) {
    this.deps = deps;
    this.licenseUrl = deps.descriptor.drm?.widevine?.licenseUrl;
    this.loadStarted = deps.deferLoad !== true;
  }

  async load(): Promise<void> {
    const { descriptor, snapToLiveOnRefocus, video, emit } = this.deps;

    // Fail fast on a DRM-required stream with no system USABLE ON THIS
    // PLATFORM, instead of loading with EME off (or a definitely-rejecting
    // drmSystems) and surfacing an opaque KEY_SYSTEM error after the manifest
    // parses. The platform signal is WebKit-ness: WebKit has FairPlay and
    // never Widevine; everything else hls.js runs on has Widevine and never
    // FairPlay. So a FairPlay-only descriptor on Chrome AND a Widevine-only
    // descriptor on Safari both fail here, with a message that names the
    // actual mismatch.
    //
    // WebKit-ness is NOT native-HLS capability, though the two look alike:
    // Chromium answers canPlayType('application/vnd.apple.mpegurl') with
    // "maybe" (measured 2026-08-07) while playing no HLS natively. Reading
    // that as WebKit made `widevineUsable` false on every Chrome session
    // carrying FairPlay material, and this guard then refused a stream
    // hls.js plays fine over Widevine.
    //
    // Known heuristic edge: some OEM/smart-TV browsers report native-HLS
    // capability AND ship Widevine (Tizen/WebOS builds). There a
    // Widevine-ONLY descriptor would fail fast although it might have
    // played. Accepted: havik-streams always emits both systems (a
    // both-systems descriptor loads fine there — FairPlay rejects at access
    // and hls.js falls back to Widevine), and those UAs are outside the
    // supported matrix. Revisit if a widevine-only + smart-TV case appears.
    const webkit = nativeHlsSupported(video) && webkitMediaStack();
    const widevineUsable = Boolean(descriptor.drm?.widevine?.licenseUrl) && !webkit;
    // FairPlay is NOT usable on this engine. WebKit binds FairPlay keys through
    // the fragment `sinf` box, so an MSE session built from a synthesized skd
    // asset ID reaches a 'usable' key and still renders nothing (macOS Safari
    // 26.5, live DRM: 1 pass / 6 attempts vs 4 / 4 on the native engine).
    // 'auto' routes FairPlay-on-Apple to NativeHlsEngine; only an explicit
    // engine:'hls' lands here, and it must fail loudly rather than play black.
    if (descriptor.drmEnabled && !widevineUsable) {
      const fairplayOnly = Boolean(descriptor.drm?.fairplay?.licenseUrl) && webkit;
      emit({
        type: 'error',
        error: new PlaybackError(
          'DRM_CLIENT',
          0,
          fairplayOnly
            ? 'FairPlay cannot be played on the hls.js engine (WebKit binds keys via the media sinf box); use engine "auto" or "native"'
            : 'DRM is required but none of the stream’s key systems is usable on this platform (WebKit takes FairPlay only; other browsers take Widevine only)',
        ),
        fatal: true,
      });
      return;
    }

    this.startHls();

    // A backgrounded tab throttles timers, so the live-sync controller can't
    // chase the edge and latency balloons while hidden. On refocus, if we're
    // well past the controller's max-latency ceiling, snap straight to live
    // rather than catch-up-playing through minutes of backlog. Opt out via
    // snapToLiveOnRefocus: false. Registered once here (not in startHls) so a
    // license-retry rebuild doesn't stack duplicate visibilitychange listeners.
    if (snapToLiveOnRefocus !== false && typeof document !== 'undefined') {
      this.onVisibility = () => {
        const h = this.hls;
        if (document.visibilityState !== 'visible' || !h) return;
        const pos = h.liveSyncPosition;
        if (typeof pos !== 'number') return;
        // maxLatency is Infinity unless a count-based max is configured (we
        // deliberately don't — it's the illegal-config trap), so key the snap
        // off the sync target instead of h.maxLatency.
        const target =
          typeof h.targetLatency === 'number' && h.targetLatency > 0 ? h.targetLatency : 3;
        if (pos - video.currentTime > Math.max(target * 3, 8)) video.currentTime = pos;
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    // Over-seek clamp. Registered here (not in startHls) for the same reason as
    // the visibility listener: a license-retry rebuild must not stack duplicates.
    this.onSeeking = () => {
      this.clampOverSeek();
      // A seek while suspended — the scrubber, the LIVE button, the refocus
      // snap above — asks for a frame the stopped loader cannot fetch, so the
      // picture would stay frozen until play(). Load it, then re-arm: five
      // seconds of loading per seek, not an open tap.
      if (this.loadSuspended) {
        this.resumeLoading();
        this.schedulePauseSuspend();
      }
    };
    video.addEventListener('seeking', this.onSeeking);

    // Pause suspension (see PAUSE_SUSPEND_MS). Same registration rule: once,
    // here, so a rebuild cannot stack the handlers.
    this.onPause = () => this.schedulePauseSuspend();
    this.onPlay = () => this.resumeLoading();
    video.addEventListener('pause', this.onPause);
    video.addEventListener('play', this.onPlay);

    // SDK-managed player-size cap (see the CAP_* constants). Registered once
    // here so a license-retry rebuild doesn't stack observers; each rebuilt
    // instance gets the cap re-applied by its MANIFEST_PARSED handler. Guarded:
    // SSR/older engines without ResizeObserver just play uncapped, exactly as
    // a caller-disabled cap would.
    if (this.sizeCapManaged && typeof ResizeObserver !== 'undefined') {
      this.capObserver = new ResizeObserver(() => this.onMediaResize());
      this.capObserver.observe(video);
      this.watchDprChanges();
    }
  }

  /**
   * A `pause` event: arm the suspension grace. Only once media loading has
   * been asked for (a deferLoad engine before its first play() fetches nothing
   * to suspend), never after stop() (the stream is over; the loader is already
   * down and must stay down), and never twice.
   */
  private schedulePauseSuspend(): void {
    if (this.pauseSuspendTimer || this.loadSuspended || this.stopped || !this.loadStarted) return;
    this.pauseSuspendTimer = setTimeout(() => {
      this.pauseSuspendTimer = null;
      const { video } = this.deps;
      // Re-checked at fire time: a play() inside the grace clears the timer,
      // but a stop() or an `ended` element between arm and fire does not.
      if (!video.paused || video.ended || this.stopped || !this.hls) return;
      try {
        this.hls.stopLoad();
      } catch {
        return; // not suspended: the loader is still running, so the watchdogs must keep watching it
      }
      this.loadSuspended = true;
    }, PAUSE_SUSPEND_MS);
  }

  /**
   * A `play` event (or a seek while suspended): cancel a pending suspension,
   * and if the loader was stopped, restart it. startLoad(-1) resumes at
   * hls.js's lastCurrentTime (the paused or seeked position); the next
   * playlist reload then re-syncs to live if the position is past the drift
   * ceiling — the ordinary go-live seek.
   */
  private resumeLoading(): void {
    if (this.pauseSuspendTimer) {
      clearTimeout(this.pauseSuspendTimer);
      this.pauseSuspendTimer = null;
    }
    if (!this.loadSuspended) return;
    this.loadSuspended = false;
    if (this.stopped || !this.hls) return;
    // The playlist was deliberately not reloaded while suspended: restart the
    // staleness and starvation clocks so the quiet window never reads as a
    // dead stream or a wedged pipeline.
    this.lastAdvanceMs = Date.now();
    this.lastFragLoadingMs = 0;
    this.starvedTicks = 0;
    // A pause/resume restarts the pipeline the same way a kick does, so a
    // kick still awaiting proof must not escalate to a rebuild on pre-pause
    // evidence (same reset stop() makes).
    this.kickPending = false;
    this.kickTicks = 0;
    try {
      this.hls.startLoad(-1);
    } catch {
      /* ignore */
    }
  }

  startLoad(): void {
    if (this.loadStarted) return;
    this.loadStarted = true;
    // The first play() of an autoplay-off player kicks the loader and then
    // calls video.play(). If that play is blocked (a gestureless probe) the
    // element stays paused with the loader running, MANIFEST_PARSED is long
    // gone and no pause event will ever come — so this is the third entry
    // point that must arm the grace. A play that goes through clears it.
    if (this.deps.video.paused) this.schedulePauseSuspend();
    // -1 = hls.js default start position: the live-sync point on live
    // playlists (the same target the armed go-live path lands on), 0 on VOD.
    // The poster card renders before MANIFEST_PARSED, so this often lands
    // pre-manifest: hls.js then sets its forceStartLoad flag and re-starts at
    // config.startPosition (hls.js default -1) once the manifest arrives —
    // identical here, but a caller hlsConfig.startPosition override would be
    // honored on the pre-manifest path only.
    this.hls?.startLoad(-1);
  }

  /**
   * Re-evaluate the cap when devicePixelRatio changes without a layout change
   * (see the dprMedia field). Re-armed after every fire because the query pins
   * the ratio that was current when it was built. Guarded feature-detection:
   * without matchMedia (SSR) or MQL addEventListener (legacy WebKit) the cap
   * simply doesn't track monitor moves — the pre-existing ResizeObserver
   * behavior, never an error.
   */
  private watchDprChanges(): void {
    if (typeof matchMedia !== 'function') return;
    this.unwatchDprChanges();
    const dpr = (typeof self !== 'undefined' && self.devicePixelRatio) || 1;
    const mql = matchMedia(`(resolution: ${dpr}dppx)`);
    if (typeof mql.addEventListener !== 'function') return;
    const handler = () => {
      this.onMediaResize();
      this.watchDprChanges();
    };
    mql.addEventListener('change', handler);
    this.dprMedia = mql;
    this.onDprChange = handler;
  }

  private unwatchDprChanges(): void {
    if (this.dprMedia && this.onDprChange) {
      this.dprMedia.removeEventListener('change', this.onDprChange);
    }
    this.dprMedia = null;
    this.onDprChange = null;
  }

  /**
   * Pull a seek that landed ahead of the live-sync position back onto it.
   *
   * Dragging the scrub bar (or a native control) to the far right lands the
   * playhead on `seekable.end()` — the bleeding edge of the live playlist, where
   * latency reads <1s and Tencent's newest parts aren't reliably published yet.
   * The forward buffer is then permanently empty: each ~0.5s part plays out
   * before the next exists, so playback advances a few frames, starves,
   * advances again. hls.js will NOT recover from this on its own — both of its
   * live-sync mechanisms only correct being too far BEHIND:
   * `maxLiveSyncPlaybackRate` rebases to 1.0x once `latency - targetLatency`
   * goes negative and never slows below it, and `liveMaxLatencyDuration`'s
   * force-seek (stream-controller's synchronizeToLiveEdge) skips out entirely
   * while `currentTime >= liveSyncPosition`. Nothing pulls the playhead back, so
   * the stutter persists for the rest of the session. This clamp is that missing
   * near-side counterpart to the drift ceiling.
   *
   * Clamping on `seeking` (not `seeked`) means the bleeding edge is never even
   * fetched. `liveSyncPosition` is the same spot seekToLive() targets, so an
   * over-seek reads as "go live" rather than as a broken seek.
   *
   * Skipped while chase suspension is active: a stale playlist is exactly the
   * state #39 stopped seeking in (the extrapolated edge points at data that
   * isn't there), and the policy there is to play out the buffer and hold the
   * last frame until 'ended' lands. An over-seek costs nothing then — there's no
   * live edge left to stutter against.
   *
   * Live only: hls.js still reports a non-null `liveSyncPosition` on VOD (it's
   * derived from liveSyncDuration, which has no VOD meaning), so gating on
   * `details.live` is what keeps this from capping VOD seeks at duration − 3s.
   *
   * Terminates without a re-entrancy flag: the clamped position is at or below
   * the limit, and the limit only moves forward, so the follow-up `seeking`
   * event this assignment queues returns at the tolerance check.
   */
  private clampOverSeek(): void {
    const hls = this.hls;
    if (!hls || this.chaseSuspended) return;
    const level = hls.levels[hls.currentLevel] ?? hls.levels[hls.loadLevel];
    if (level?.details?.live !== true) return;
    const limit = hls.liveSyncPosition;
    if (typeof limit !== 'number' || !Number.isFinite(limit)) return;
    const { video } = this.deps;
    if (video.currentTime <= limit + OVER_SEEK_TOLERANCE_S) return;
    video.currentTime = limit;
  }

  /**
   * Build (or rebuild) the hls.js instance, wire its events, and start loading.
   * Split out of load() so a transient license-request failure can re-drive EME
   * with a fresh instance (new key session → new license request) without
   * re-adding the one-time visibility listener.
   */
  private startHls(): void {
    const { video, descriptor, credential, deviceId, userId, lowLatency, debug, hlsConfig, emit } =
      this.deps;

    // Per-instance watchdog state. A rebuilt instance has issued no kick and
    // has loaded no fragment, so carrying either across would let the first
    // tick after a rebuild escalate again on the previous instance's evidence.
    this.kickPending = false;
    this.kickTicks = 0;
    this.starvedTicks = 0;
    this.lastFragLoadingMs = 0;
    // The end-of-stream stamps are per-instance for a sharper reason: trackEnd
    // only restamps lastAdvanceMs when endSN CHANGES, so a rebuilt instance
    // whose first playlist load repeats the endSN the old one last saw would
    // keep ageing an advance stamp from before the rebuild, and can cross
    // CHASE_SUSPEND_STALE_MS — or END_STALE_MS, reporting a healthy stream as
    // ended. Zeroing them gives the new instance a fresh liveness window:
    // checkEndStale returns early while lastAdvanceMs is 0, and checkStarvation
    // treats the playlist as not-yet-alive, so both watchdogs stay disarmed
    // until this instance sees its own first load. The ENDLIST path
    // (live→false) and the server's SSE push still catch a real end meanwhile.
    this.lastEndSN = -1;
    this.lastAdvanceMs = 0;

    const drmEnabled =
      descriptor.drmEnabled &&
      Boolean(descriptor.drm?.widevine?.licenseUrl || descriptor.drm?.fairplay?.licenseUrl);
    // FairPlay is only offered where WebKit can actually take it (Safari /
    // iOS — the same signal as native-HLS capability). This also scopes
    // drmSystemOptions below: the option object is GLOBAL in hls.js (applied
    // to every key system's requestMediaKeySystemAccess), and WebKit's
    // supportedRobustnesses() for FairPlay is { '' } — handing it the
    // Widevine SW_SECURE_* pins makes the FairPlay access request reject.

    const config: Partial<HlsConfig> = {
      // hls.js internal logging (fragment/part scheduling, ABR switches, EME
      // key sessions + license traffic) — opt-in, for diagnosing a playback
      // problem with support. The operator Demo Player (havik-ops
      // streams-player.component.ts) hardcodes this on; it is the one key the
      // two configs are meant to differ on, which is why it is an option here
      // rather than a constant.
      debug: debug === true,
      // Workers are disabled in low-latency mode: on hls.js 1.6.x the
      // LL-HLS partial-segment append path hits a bufferAppendError race
      // ("SourceBuffer is still processing an appendBuffer/remove") that
      // reproduces ~100% with workers ON (video-dev/hls.js#7321). Disabling
      // the worker is the documented mitigation; standard (non-LL) playback
      // keeps workers for off-main-thread parsing. Re-enable once a hls.js
      // release fixes the worker append race.
      enableWorker: !lowLatency,
      lowLatencyMode: lowLatency,
      backBufferLength: 90,
      // Audio-hole bridge. Tencent's LL-HLS audio packager cuts a ~0.45s
      // "remainder" part at each segment boundary; the hls.js default
      // maxBufferHole (0.1s) is smaller than the hole, so the player stalls
      // and gap-skips ~0.5s every few seconds. 0.5 bridges it as a
      // sub-perceptible hitch instead of a rebuffer.
      maxBufferHole: 0.5,
      // Live-edge target (liveSyncDuration) + drift ceiling (liveMaxLatencyDuration)
      // are applied by normalizeLiveSync() AFTER the caller's hlsConfig is merged
      // below — so a raw override (incl. the Count variants) can never produce an
      // illegal pair that throws at construction. See that helper for the why.
      // Catch up at up to 1.5x (ramped by how far behind) to recover drift inside
      // the target..ceiling band before the hard seek. Internal — not a public
      // knob (1.0 silently re-introduces drift); overridable via raw hlsConfig.
      maxLiveSyncPlaybackRate: 1.5,
      // ABR start quality — start conservative, then ramp. Seeding high (~4 Mbps
      // → a ~720p first rendition) and relying on "drop fast" to recover was still
      // rebuffering in the field on constrained links: the high first grab stalls
      // to fill the buffer before ABR can drop. Per the root-cause investigation
      // (docs/llhls-buffering-investigation.md §3), seed ~1 Mbps so the first
      // rendition is one a modest link sustains (~360p) and ABR ramps up within a
      // few seconds when real bandwidth allows — trading a slightly softer first
      // ~2s for not stalling on startup (the first-20s buffering we were chasing).
      // The SDK-managed size cap still bounds the session to the player's
      // pixel box (below). Caller startLevel / maxBitrate still override.
      abrEwmaDefaultEstimate: 1_000_000,
      // Player-size capping is SDK-managed (see the CAP_* constants): hls.js's
      // own poller applies a new cap the instant the box changes, which turns
      // every fullscreen enter/exit into immediate ABR switches — each a
      // visible ~1s stall at LL buffer depth. Explicit false (the hls.js
      // default) so the two managers never fight over autoLevelCapping; a
      // caller hlsConfig `capLevelToPlayerSize: true` opts back into the
      // hls.js poller and stands the SDK manager down.
      capLevelToPlayerSize: false,
      // deferLoad (autoplay-off pre-play): parse the manifest (MANIFEST_PARSED
      // → 'ready', track lists) but fetch no level playlists or fragments —
      // and so no EME/license traffic — until startLoad(). Keyed off
      // loadStarted, not deps.deferLoad, so a license-retry rebuild after
      // playback began loads eagerly again. The hlsConfig escape hatch (spread
      // below) wins here like everywhere else — an explicit autoStartLoad:
      // true is the "warm the buffer, wait for the gesture" pattern;
      // loadStarted is re-synced to the final merged config after the spread.
      ...(this.loadStarted ? {} : { autoStartLoad: false }),
      ...(drmEnabled
        ? {
            emeEnabled: true,
            drmSystems: {
              ...(this.licenseUrl ? { 'com.widevine.alpha': { licenseUrl: this.licenseUrl } } : {}),
            },
            // FairPlay is NOT configured here — it lives on NativeHlsEngine.
            // WebKit binds FairPlay keys through the fragment `sinf` box, so an
            // MSE session built from a synthesized skd asset ID reaches a
            // 'usable' key and still renders nothing. load() fails fast above
            // rather than reaching this config.
            drmSystemOptions: {
              videoRobustness: 'SW_SECURE_DECODE',
              audioRobustness: 'SW_SECURE_CRYPTO',
              videoEncryptionScheme: 'cbcs',
              audioEncryptionScheme: 'cbcs',
            },
            // Reuse this element's CDM across rebuilds. Chrome refuses to
            // replace OR remove a media element's ContentDecryptionModule once
            // the element has a player, so every rebuildInstance() below —
            // starvation, license retry, or a managed reload() building a fresh
            // engine on the same <video> — would otherwise die on
            // `setMediaKeys`: "The existing ContentDecryptionModule object
            // cannot be removed at this time", surfaced as a fatal
            // KEY_SYSTEM_NO_KEYS. hls.js's CDMCleanupPromise cannot help; its
            // cleanup call is forbidden too. See core/drm/cdm.ts.
            //
            // Only when EME exists: with no key-system API at all (a plain-http
            // embed — EME is secure-context-only), leaving the hook unset lets
            // hls.js report that cause itself rather than us shadowing it.
            ...(emeAvailable()
              ? { requestMediaKeySystemAccessFunc: reuseMediaKeysFor(video) }
              : {}),
            // EME license requests route through licenseXhrSetup — NOT the
            // segment-side xhrSetup (a hard-won Demo Player lesson; putting the
            // key in xhrSetup makes every license 401). Open the XHR ourselves
            // before setting any headers (setRequestHeader throws if the XHR
            // isn't OPENED) so we never depend on hls.js's internal open-ordering;
            // opening against the CURRENT license URL also picks up a refreshed
            // signed URL (hls.js keeps passing the construction-time one).
            // Widevine only — FairPlay lives on the native engine.
            licenseXhrSetup: (xhr: XMLHttpRequest, url: string) => {
              xhr.open('POST', this.licenseUrl ?? url, true);
              xhr.setRequestHeader('x-api-key', credential.apiKey);
              xhr.setRequestHeader('X-Match-Urn', descriptor.matchUrn);
              xhr.setRequestHeader('X-Device-Id', deviceId);
              if (userId) xhr.setRequestHeader('X-User-Id', userId);
              // License-acquisition timing for the QoE plane — fleet-wide DRM
              // regressions (WAF license 403s, the canPlayType trap) were
              // invisible without it.
              this.licenseRequested = true;
              const t0 = performance.now();
              xhr.addEventListener('loadend', () => {
                this.licenseTimeMs = Math.max(1, Math.round(performance.now() - t0));
              });
            },
          }
        : {}),
      // CMCD in query mode (plan D1/D2): the per-session sid rides every CDN
      // request as the attribution key the edge-log plane joins on. Query
      // mode, never headers — header mode would force a CORS preflight per
      // segment. Only when streams minted a sid (server-side analytics flag).
      ...(descriptor.analytics?.sid
        ? {
            cmcd: {
              sessionId: descriptor.analytics.sid,
              contentId: descriptor.matchUrn,
              useHeaders: false,
            },
          }
        : {}),
      ...hlsConfig,
    };
    // The FairPlay certificate GET goes through hls.js's GENERIC loader (not
    // licenseXhrSetup), and drm-proxy requires the api key on it (its router
    // test asserts 401 without). Strictly URL-scoped: manifest/segment
    // requests are public CDN fetches and must not carry auth. hls.js hands
    // xhrSetup an UNOPENED xhr — open it first or setRequestHeader throws.
    // COMPOSED with any caller hlsConfig.xhrSetup (applied post-spread): the
    // escape hatch must not silently disable certificate auth on WebKit.
    // ORDER MATTERS: the caller runs FIRST — it gets the unopened xhr hls.js
    // hands out (the documented contract, and many hooks call open()
    // themselves; the XHR spec EMPTIES author headers on open(), so auth set
    // before a caller's open() would be silently wiped). Our cert branch then
    // opens only if still UNSENT and sets the api key on the final, opened xhr.
    // Resolve the live-edge target/ceiling on the FINAL merged config so a raw
    // hlsConfig override can't produce an illegal hls.js pair (see helper).
    normalizeLiveSync(config);
    // The escape hatch wins: a caller that re-enabled hls.js's own player-size
    // poller gets exactly that, and the SDK manager stands down (two writers on
    // autoLevelCapping would fight, the 1s poller always winning).
    this.sizeCapManaged = config.capLevelToPlayerSize !== true;
    // Sync loadStarted with the FINAL config: a caller hlsConfig.autoStartLoad
    // may have overridden the deferLoad key above, and a stale flag would make
    // startLoad() re-kick a loader that is already running (or believe a
    // caller-stopped one had started).
    this.loadStarted = config.autoStartLoad !== false;

    const hls = new Hls(config);
    this.hls = hls;

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this.mediaRecoveries = 0;
      this.networkRecoveries = 0;
      const heights = hls.levels.map((l) => l.height).filter((h): h is number => !!h);
      this.ladderHeights = heights.length ? [...new Set(heights)].sort((a, b) => a - b) : undefined;
      this.ladderTopBitrateKbps = hls.levels.length
        ? Math.round(Math.max(...hls.levels.map((l) => l.bitrate)) / 1000)
        : 0;
      // Initial size cap, applied immediately (no settle window): the first
      // fetch of a small/embedded player must not grab 1080p.
      this.applySizeCap();
      // An instance that starts paused — autoplay still pending, autoplay
      // BLOCKED (the play card, with hls.js chasing live behind it forever:
      // no `pause` event ever fires for an element that never played), or a
      // rebuild while suspended (the new instance loads regardless) — gets
      // the same grace as a pause. Pending autoplay resolves long before it
      // fires, and the fire-time re-check sees the element playing.
      this.loadSuspended = false;
      if (video.paused) this.schedulePauseSuspend();
      emit({ type: 'ready' });
    });
    // After a stretch of uninterrupted buffering, refund the recovery budget so a
    // healthy long session isn't permanently capped. A fresh error cancels the
    // pending refund (see clearRecoveryReset in onError), preserving tight-loop
    // protection. Main-track fragments only: the starvation wedge keeps audio
    // buffering while video is dead, and audio frags must not refund the kicks.
    hls.on(Hls.Events.FRAG_BUFFERED, (_e, data) => {
      if (data?.frag?.type !== 'main') return;
      // Main-track media buffered, so the last kick did its job: cancel the
      // pending escalation. This is the only place the grace is cleared, which
      // is what makes the escalation fire on exactly the case it is for — a
      // kick that produced no video, rather than one that merely took a while.
      this.kickPending = false;
      this.kickTicks = 0;
      if (
        (this.mediaRecoveries > 0 ||
          this.networkRecoveries > 0 ||
          this.licenseRecoveries > 0 ||
          this.starvationKicks > 0 ||
          this.starvationRebuilds > 0) &&
        !this.recoveryResetTimer
      ) {
        this.recoveryResetTimer = setTimeout(() => {
          this.mediaRecoveries = 0;
          this.networkRecoveries = 0;
          this.licenseRecoveries = 0;
          this.starvationKicks = 0;
          this.starvationRebuilds = 0;
          this.recoveryResetTimer = null;
        }, RECOVERY_RESET_MS);
      }
    });
    // Starvation-watchdog liveness input: a healthy or merely rebuffering
    // pipeline keeps issuing main-track fragment loads; a wedged one goes quiet.
    hls.on(Hls.Events.FRAG_LOADING, (_e, data) => {
      if (data?.frag?.type === 'main') this.lastFragLoadingMs = Date.now();
    });
    // Consumption accounting: media segment payload bytes (all tracks — the
    // dashboard-grade half of D3; edge logs stay the billing truth).
    hls.on(Hls.Events.FRAG_LOADED, (_e, data) => {
      const stats = data?.frag?.stats;
      const loaded = stats?.loaded;
      if (typeof loaded === 'number') this.bytesLoaded += loaded;
      // Per-request delivery timing: the viewer-side view of how the CDN is
      // serving this session, which the edge plane's own latency cannot show
      // (it never sees the last mile).
      const loading = stats?.loading;
      if (loading && loading.end > 0 && loading.start > 0) {
        this.requestCount += 1;
        this.requestTimesMs.push(Math.max(0, Math.round(loading.end - loading.start)));
        if (this.requestTimesMs.length > MAX_REQUEST_SAMPLES) this.requestTimesMs.shift();
      }
      const url = data?.frag?.url;
      if (url) {
        const host = hostOf(url);
        // Re-read every fragment on purpose: a mid-session CDN failover is
        // exactly the event this column exists to make visible.
        if (host) this.cdnHost = host;
      }
    });
    hls.on(Hls.Events.LEVEL_LOADED, (_e, data: LevelLoadedData) => {
      // Playlist chatter is nontrivial at 2s LL refresh windows — counted
      // separately from media bytes (plan §3.1).
      const loaded = data?.stats?.loaded;
      if (typeof loaded === 'number') this.playlistBytesLoaded += loaded;
      if (this.detectedLowLatency) return;
      const details = data.details;
      const isLL = Boolean(details?.partList?.length) || (details?.partTarget ?? 0) > 0;
      if (isLL) {
        this.detectedLowLatency = true;
        emit({ type: 'lowLatencyDetected', value: true });
      }
    });
    // End-of-stream detection (separate handler so the LL-detection early-return
    // above never short-circuits it). ENDLIST flips details.live→false; a frozen
    // manifest is caught by the staleness watchdog interval below.
    hls.on(Hls.Events.LEVEL_LOADED, (_e, data: LevelLoadedData) => this.trackEnd(data));
    // startHls() also runs on a rebuild, and assigning over a live handle
    // leaks the previous interval: both keep firing, so every checkEndStale /
    // checkStarvation tick doubles up. That already applied to the
    // license-retry rebuild (up to MAX_LICENSE_RECOVERIES extra intervals);
    // it would also break the starvation escalation, whose grace is measured
    // between ticks — a doubled tick rate re-stamps the kick before the grace
    // can ever elapse.
    if (this.endWatchdog) clearInterval(this.endWatchdog);
    this.endWatchdog = setInterval(() => {
      this.checkEndStale();
      this.checkStarvation();
    }, END_WATCH_INTERVAL_MS);
    hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
      // Rendition is only sampled at heartbeats; without this counter, ABR
      // churn between samples is invisible (plan §3.1).
      this.renditionSwitches += 1;
      // Direction by BITRATE, not by index: level order is a property of
      // the manifest, not a contract, and a reordered reload would invert
      // every switch this session reports.
      const from = this.lastLevelIndex >= 0 ? hls.levels[this.lastLevelIndex] : undefined;
      const to = hls.levels[data.level];
      if (from && to && to.bitrate !== from.bitrate) {
        if (to.bitrate > from.bitrate) this.upshifts += 1;
        else this.downshifts += 1;
      }
      this.lastLevelIndex = data.level;
      emit({ type: 'qualitychange', index: data.level });
    });
    hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, (_e, data) =>
      emit({ type: 'audiotrackchange', id: data.id }),
    );
    hls.on(Hls.Events.SUBTITLE_TRACK_SWITCH, (_e, data) =>
      emit({ type: 'texttrackchange', id: data.id }),
    );
    hls.on(Hls.Events.ERROR, (_e, data: ErrorData) => this.onError(data));

    hls.loadSource(descriptor.manifestUrl);
    hls.attachMedia(video);
  }

  getQualityLevels(): QualityLevel[] {
    if (!this.hls) return [];
    return this.hls.levels.map((l, index) => ({
      index,
      width: l.width || undefined,
      height: l.height || undefined,
      bitrate: l.bitrate,
      codecs: l.videoCodec || l.codecSet || undefined,
    }));
  }

  getCurrentQuality(): number {
    if (!this.hls) return -1;
    return this.hls.autoLevelEnabled ? -1 : this.hls.currentLevel;
  }

  setQuality(index: number): void {
    if (this.hls) this.hls.currentLevel = index; // -1 re-enables ABR
  }

  setMaxBitrate(bitrate: number | null): void {
    const hls = this.hls;
    if (!hls) return;
    if (bitrate == null) {
      this.bitrateCapLevel = -1;
    } else {
      // Cap ABR at the highest level whose bitrate fits the budget.
      let cap = -1;
      hls.levels.forEach((l, i) => {
        if (l.bitrate <= bitrate) cap = i;
      });
      this.bitrateCapLevel = cap;
    }
    // Routed through pushCap so clearing the bitrate budget no longer wipes the
    // player-size cap (both used to write autoLevelCapping directly, last one
    // winning until hls.js's 1s poller re-imposed the size cap).
    this.pushCap();
  }

  /**
   * Level index the player's current pixel box caps ABR to, or null when it
   * can't be computed (no levels yet, or a zero-size/hidden box — upstream
   * skips those the same way). Selection is hls.js's own
   * CapLevelController.getMaxLevelByMediaSize, on the element's bounding box
   * scaled by the same DPR rules the upstream poller uses
   * (ignoreDevicePixelRatio / maxDevicePixelRatio from the merged config), so
   * flipping between the two managers can never change WHICH level is chosen —
   * only WHEN it is applied.
   */
  private computeSizeCapTarget(): number | null {
    const hls = this.hls;
    if (!hls || hls.levels.length === 0) return null;
    const { video } = this.deps;
    const rect = video.getBoundingClientRect();
    let width = rect.width;
    let height = rect.height;
    if (!width && !height) {
      // Mirror upstream getDimensions(): a media element that isn't laid out
      // (equivalent to not being in the DOM) falls back to its width/height
      // attributes — without this, off-DOM playback would never receive an
      // initial cap, since ResizeObserver doesn't fire for detached elements.
      width = video.width || 0;
      height = video.height || 0;
    }
    if (!(width > 0) || !(height > 0)) return null;
    const cfg: Partial<HlsConfig> = hls.config ?? {};
    let scale = 1;
    if (!cfg.ignoreDevicePixelRatio && typeof self !== 'undefined' && self.devicePixelRatio > 0) {
      scale = self.devicePixelRatio;
    }
    scale = Math.min(scale, cfg.maxDevicePixelRatio ?? Number.POSITIVE_INFINITY);
    return CapLevelController.getMaxLevelByMediaSize(hls.levels, width * scale, height * scale);
  }

  /**
   * ResizeObserver callback: debounce cap changes so only a SETTLED box ever
   * reaches ABR. Every size event restarts the pending window, so a fullscreen
   * transition animating through intermediate sizes keeps deferring until the
   * box stops moving — and a box that settles back onto the applied cap (exit
   * + prompt re-enter) cancels the pending change without ever touching ABR.
   */
  private onMediaResize(): void {
    if (!this.sizeCapManaged) return;
    const target = this.computeSizeCapTarget();
    if (target == null) return;
    if (this.sizeCapLevel == null) {
      // Nothing applied yet (manifest parsed while the box was hidden/zero):
      // this is the initial cap, not a change — apply immediately.
      this.applySizeCap();
      return;
    }
    if (target === this.sizeCapLevel) {
      if (this.capTimer) {
        clearTimeout(this.capTimer);
        this.capTimer = null;
      }
      return;
    }
    if (this.capTimer) clearTimeout(this.capTimer);
    const delay = target > this.sizeCapLevel ? CAP_RAISE_SETTLE_MS : CAP_SHRINK_LAZY_MS;
    this.capTimer = setTimeout(() => {
      this.capTimer = null;
      // The box can move between the last observed event and this firing
      // (ResizeObserver delivery is frame-aligned, timers aren't): apply only
      // if the box still selects the level this window was scheduled FOR;
      // otherwise re-debounce in the fresh direction — a raise window must
      // never be the door a shrink walks through.
      if (this.computeSizeCapTarget() === target) this.applySizeCap();
      else this.onMediaResize();
    }, delay);
  }

  /** Recompute the size cap from the CURRENT box and apply it (recomputing at
   *  timer fire, not schedule time, so a stale pending value is never applied). */
  private applySizeCap(): void {
    if (!this.sizeCapManaged) return;
    if (this.capTimer) {
      clearTimeout(this.capTimer);
      this.capTimer = null;
    }
    const target = this.computeSizeCapTarget();
    if (target == null) return;
    this.sizeCapLevel = target;
    this.pushCap();
  }

  /** Combine the size cap and the setMaxBitrate cap (min wins) into hls.js's
   *  single autoLevelCapping knob. */
  private pushCap(): void {
    const hls = this.hls;
    if (!hls) return;
    const size = this.sizeCapLevel ?? -1;
    const rate = this.bitrateCapLevel;
    hls.autoLevelCapping = size < 0 ? rate : rate < 0 ? size : Math.min(size, rate);
  }

  getAudioTracks(): AudioTrackInfo[] {
    if (!this.hls) return [];
    return this.hls.audioTracks.map((t) => ({
      id: t.id,
      name: t.name,
      lang: t.lang || undefined,
      default: Boolean(t.default),
    }));
  }

  setAudioTrack(id: number): void {
    if (this.hls) this.hls.audioTrack = id;
  }

  getTextTracks(): TextTrackInfo[] {
    if (!this.hls) return [];
    return this.hls.subtitleTracks.map((t) => ({
      id: t.id,
      name: t.name,
      lang: t.lang || undefined,
    }));
  }

  setTextTrack(id: number): void {
    const hls = this.hls;
    if (!hls) return;
    if (id < 0) {
      hls.subtitleTrack = -1;
      hls.subtitleDisplay = false;
    } else {
      hls.subtitleTrack = id;
      hls.subtitleDisplay = true;
    }
  }

  seekToLive(): void {
    const hls = this.hls;
    if (hls && typeof hls.liveSyncPosition === 'number') {
      this.deps.video.currentTime = hls.liveSyncPosition;
    }
  }

  setLicenseUrls(urls: { widevine?: string; fairplay?: string }): void {
    // Refresh only what the caller has — licenseXhrSetup reads this field per
    // request, so an in-flight session picks the new signed URL up on its next
    // license POST without an instance rebuild. FairPlay is ignored here: it
    // runs on NativeHlsEngine, which keeps its own refreshed URL.
    if (urls.widevine) this.licenseUrl = urls.widevine;
  }

  getStats(): PlaybackStats {
    const hls = this.hls;
    const frames = readFrameStats(this.deps.video);
    const stats: PlaybackStats = {
      droppedFrames: frames.dropped,
      decodedFrames: frames.totalFrames,
      lowLatency: this.detectedLowLatency,
      bytesLoaded: this.bytesLoaded,
      playlistBytesLoaded: this.playlistBytesLoaded,
      renditionSwitches: this.renditionSwitches,
      licenseTimeMs: this.licenseTimeMs,
      upshifts: this.upshifts,
      downshifts: this.downshifts,
      requestCount: this.requestCount,
      requestErrors: this.requestErrors,
      // A copy: PlaybackStats is public surface, and a consumer that
      // mutated this array would corrupt the engine's own accounting.
      requestTimesMs: [...this.requestTimesMs],
      ladderHeights: this.ladderHeights,
      ladderTopBitrateKbps: this.ladderTopBitrateKbps || undefined,
    };
    if (this.cdnHost) stats.cdnHost = this.cdnHost;
    // Widevine with the robustness this engine requests (SW_SECURE_*) is
    // software-path by definition: L3. A hardware-backed L1 is not reachable
    // through hls.js/MSE here, so reporting the requested level is honest.
    if (this.licenseRequested) {
      stats.keySystem = 'widevine';
      stats.drmSecurityLevel = 'L3';
    }
    if (hls) {
      stats.qualityPinned = !hls.autoLevelEnabled;
      if (typeof hls.latency === 'number') stats.latencySeconds = hls.latency;
      if (typeof hls.targetLatency === 'number' && hls.targetLatency > 0) {
        stats.targetLatencySeconds = hls.targetLatency;
      }
      if (typeof hls.bandwidthEstimate === 'number') {
        stats.bandwidthKbps = Math.round(hls.bandwidthEstimate / 1000);
      }
      const level = hls.currentLevel >= 0 ? hls.levels[hls.currentLevel] : undefined;
      if (level) stats.levelHeight = level.height;
      if (level?.videoCodec) stats.videoCodec = level.videoCodec;
      if (level?.audioCodec) stats.audioCodec = level.audioCodec;
      if (level && typeof level.bitrate === 'number') {
        stats.renditionBitrateKbps = Math.round(level.bitrate / 1000);
      }
      if (typeof level?.details?.live === 'boolean') {
        stats.isLive = level.details.live;
        if (typeof hls.liveSyncPosition === 'number') {
          stats.atLiveEdge = this.deps.video.currentTime >= hls.liveSyncPosition - 1.5;
        }
      }
    }
    return stats;
  }

  /**
   * Track live-playlist progress for end detection. Once the stream has been
   * seen LIVE, a transition to `details.live === false` means the origin
   * published #EXT-X-ENDLIST (clean end); otherwise the staleness watchdog
   * (checkEndStale) catches a frozen-but-200 manifest.
   */
  private trackEnd(data: LevelLoadedData): void {
    const d = data.details;
    if (!d) return;
    if (d.live === true) {
      this.sawLive = true;
      const sn = d.endSN ?? -1;
      if (sn !== this.lastEndSN) {
        this.lastEndSN = sn;
        this.lastAdvanceMs = Date.now();
        // The playlist is moving again — restore normal live-edge chasing
        // before the stale window that suspended it is forgotten.
        if (this.chaseSuspended) this.resumeChasing();
      }
      return;
    }
    // VOD that was never live is not an "end" — only treat live→false as ENDLIST.
    if (this.sawLive) this.emitEnded('endlist');
  }

  private checkEndStale(): void {
    if (this.endedEmitted || !this.sawLive || this.lastAdvanceMs === 0) return;
    // Chrome's intensive throttling (muted tab hidden >5 min) batches ALL timers
    // to ~1/min — including hls.js's playlist-reload timer — so advance stamps
    // gap to ~60s on a healthy stream (this check wakes in the same batch as the
    // reload, before its XHR completes). Re-stamp instead of evaluating while
    // hidden: staleness detection resumes with a fresh window on refocus, and
    // the SSE live-state push still catches a real end immediately. Mirrors the
    // Demo Player guard (havik-ops#68).
    if (typeof document !== 'undefined' && document.hidden) {
      this.lastAdvanceMs = Date.now();
      return;
    }
    // Same re-stamp while loading is suspended for a pause: no playlist is
    // being reloaded, so "not advancing" is by design, not a frozen origin.
    if (this.loadSuspended) {
      this.lastAdvanceMs = Date.now();
      return;
    }
    const staleMs = Date.now() - this.lastAdvanceMs;
    if (!this.chaseSuspended && staleMs > CHASE_SUSPEND_STALE_MS) this.suspendChasing();
    if (staleMs > END_STALE_MS) this.emitEnded('stalled');
  }

  /**
   * Detect and heal a wedged hls.js video pipeline (see the STARVATION_*
   * constants for the mechanism). All three conditions must hold across
   * consecutive ticks:
   *  - the element is starved (readyState < HAVE_FUTURE_DATA) while playing;
   *  - the live playlist is still advancing (a stale playlist is stream end —
   *    the end watchdog and chase suspension own that path);
   *  - hls.js has issued no main-track fragment load recently (a plain
   *    rebuffer keeps FRAG_LOADING events flowing; the wedge goes silent).
   * Hidden tabs are skipped for the same timer-throttling reason as
   * checkEndStale. The kick budget is windowed like the other recoveries
   * (refunded after RECOVERY_RESET_MS of healthy buffering).
   */
  private checkStarvation(): void {
    const hls = this.hls;
    const { video } = this.deps;
    if (!hls || this.endedEmitted || !this.sawLive || this.chaseSuspended) return;
    if ((typeof document !== 'undefined' && document.hidden) || this.loadSuspended) {
      this.starvedTicks = 0;
      return;
    }
    const playlistAlive =
      this.lastAdvanceMs !== 0 && Date.now() - this.lastAdvanceMs < CHASE_SUSPEND_STALE_MS;
    const fragQuiet =
      this.lastFragLoadingMs !== 0 &&
      Date.now() - this.lastFragLoadingMs > STARVATION_FRAG_QUIET_MS;
    const starved = video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA && !video.paused;
    if (!(starved && playlistAlive && fragQuiet)) {
      this.starvedTicks = 0;
      return;
    }
    // Reached only on a visible tick that still shows the wedge signature, so
    // this is the grace's clock: active starvation, not elapsed wall time.
    if (this.kickPending) this.kickTicks += 1;
    if (++this.starvedTicks < STARVATION_TICKS) return;
    this.starvedTicks = 0;

    // A kick that has had its grace and still produced no main-track media did
    // not reach the wedge. Escalate to a rebuild before spending the rest of
    // the kick budget re-parking the same instance.
    if (this.kickPending && this.kickTicks >= STARVATION_KICK_GRACE_TICKS) {
      this.kickPending = false;
      this.kickTicks = 0;
      if (this.starvationRebuilds < MAX_STARVATION_REBUILDS) {
        this.starvationRebuilds += 1;
        console.warn(
          'havik-player: restarting the load did not resume video (the wedge is in hls.js state that only a rebuild clears) — rebuilding the player',
        );
        this.rebuildInstance('starvation rebuild');
        return;
      }
    }

    if (this.starvationKicks >= MAX_STARVATION_KICKS) return;
    this.starvationKicks += 1;
    this.kickPending = true;
    this.kickTicks = 0;
    console.warn(
      'havik-player: video starved while the live playlist advances (wedged hls.js fragment pipeline) — restarting the load',
    );
    try {
      hls.stopLoad();
      hls.startLoad(-1);
    } catch (err) {
      // The kick is best-effort: a throw from a wedged hls.js must not escape
      // the watchdog interval and take the page down with it.
      console.warn('havik-player: starvation kick failed', err);
    }
  }

  /**
   * Freeze live-edge chasing on a stale playlist. Both knobs are read
   * dynamically by hls.js's LatencyController on every tick, so mutating the
   * live config takes effect immediately: an effectively-infinite latency
   * ceiling disables the force-seek (which would land in data that doesn't
   * exist), and a 1.0 sync rate stops the catch-up speedup — the video simply
   * plays out its buffer and holds the last frame until 'ended' (or fresh
   * bytes) arrive.
   */
  private suspendChasing(): void {
    const hls = this.hls;
    if (!hls) return;
    this.chaseSuspended = true;
    this.savedLiveMaxLatency = hls.config.liveMaxLatencyDuration;
    this.savedMaxSyncRate = hls.config.maxLiveSyncPlaybackRate;
    hls.config.liveMaxLatencyDuration = Number.MAX_SAFE_INTEGER;
    hls.config.maxLiveSyncPlaybackRate = 1;
  }

  private resumeChasing(): void {
    this.chaseSuspended = false;
    const hls = this.hls;
    if (!hls) return;
    // Restore UNCONDITIONALLY — including `undefined`. Count-based caller
    // configs (liveSyncDurationCount) have their seconds keys deleted by
    // normalizeLiveSync, so the saved value is legitimately undefined; a
    // guarded restore would leave the suspension's MAX_SAFE_INTEGER ceiling
    // in place forever (hls.js prefers the seconds key over the count key),
    // silently disabling the drift ceiling for the rest of the session.
    hls.config.liveMaxLatencyDuration = this.savedLiveMaxLatency as number;
    hls.config.maxLiveSyncPlaybackRate = this.savedMaxSyncRate as number;
  }

  private emitEnded(reason: 'endlist' | 'stalled'): void {
    if (this.endedEmitted) return;
    this.endedEmitted = true;
    if (this.endWatchdog) {
      clearInterval(this.endWatchdog);
      this.endWatchdog = null;
    }
    this.deps.emit({ type: 'ended', reason });
  }

  stop(): void {
    if (this.endWatchdog) {
      clearInterval(this.endWatchdog);
      this.endWatchdog = null;
    }
    // A deliberate stop is not a wedge: drop any pending escalation so a
    // later resume can't rebuild on evidence from before the stop.
    this.kickPending = false;
    this.kickTicks = 0;
    // And it outranks the pause suspension: the `pause` this stop fires must
    // not arm a grace, and a later `play` on the ended element (the control
    // bar stays clickable) must not restart the loader.
    this.stopped = true;
    this.loadSuspended = false;
    if (this.pauseSuspendTimer) {
      clearTimeout(this.pauseSuspendTimer);
      this.pauseSuspendTimer = null;
    }
    try {
      this.hls?.stopLoad();
    } catch {
      /* ignore */
    }
    try {
      this.deps.video.pause();
    } catch {
      /* ignore */
    }
  }

  destroy(): void {
    this.clearRecoveryReset();
    if (this.capTimer) {
      clearTimeout(this.capTimer);
      this.capTimer = null;
    }
    if (this.capObserver) {
      this.capObserver.disconnect();
      this.capObserver = null;
    }
    this.unwatchDprChanges();
    if (this.endWatchdog) {
      clearInterval(this.endWatchdog);
      this.endWatchdog = null;
    }
    if (this.onVisibility && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.onVisibility = null;
    }
    if (this.onSeeking) {
      this.deps.video.removeEventListener('seeking', this.onSeeking);
      this.onSeeking = null;
    }
    if (this.onPause) {
      this.deps.video.removeEventListener('pause', this.onPause);
      this.onPause = null;
    }
    if (this.onPlay) {
      this.deps.video.removeEventListener('play', this.onPlay);
      this.onPlay = null;
    }
    if (this.pauseSuspendTimer) {
      clearTimeout(this.pauseSuspendTimer);
      this.pauseSuspendTimer = null;
    }
    if (this.networkRetryTimer) {
      clearTimeout(this.networkRetryTimer);
      this.networkRetryTimer = null;
    }
    if (this.licenseRetryTimer) {
      clearTimeout(this.licenseRetryTimer);
      this.licenseRetryTimer = null;
    }
    if (this.hls) {
      try {
        this.hls.destroy();
      } catch (e) {
        // Surface a failed teardown rather than silently leaking the worker/EME session.
        console.warn('havik-player: hls.js destroy() threw during teardown', e);
      }
      this.hls = null;
    }
  }

  private clearRecoveryReset(): void {
    if (this.recoveryResetTimer) {
      clearTimeout(this.recoveryResetTimer);
      this.recoveryResetTimer = null;
    }
  }

  /**
   * A transient transport failure of the EME license (or service-certificate)
   * request — no response, timeout, 429, or 5xx — is recoverable: hls.js has no
   * license-retry policy and reports it as a fatal KEY_SYSTEM error, so we
   * rebuild the instance (a fresh key session re-issues the request), bounded
   * and backed off. A real auth/entitlement denial (401/403 or any other 4xx)
   * is NOT retried. Returns true iff a retry was scheduled.
   */
  private scheduleLicenseRetry(data: ErrorData): boolean {
    const requestFailure =
      data.details === Hls.ErrorDetails.KEY_SYSTEM_LICENSE_REQUEST_FAILED ||
      data.details === Hls.ErrorDetails.KEY_SYSTEM_SERVER_CERTIFICATE_REQUEST_FAILED;
    if (!requestFailure) return false;
    // 0 = network/CORS/timeout (the POST never got a response); 408/429/5xx are
    // transient server-side. Any other 4xx (esp. 401/403) is a real denial.
    const status = data.response?.code ?? 0;
    const transient = status === 0 || status === 408 || status === 429 || status >= 500;
    if (!transient || this.licenseRecoveries >= MAX_LICENSE_RECOVERIES) return false;
    this.licenseRecoveries += 1;
    const delay = Math.min(8000, 500 * 2 ** (this.licenseRecoveries - 1));
    this.licenseRetryTimer = setTimeout(() => {
      this.licenseRetryTimer = null;
      this.rebuildInstance('license retry'); // fresh key session re-issues the request
    }, delay);
    return true;
  }

  /**
   * Tear the hls.js instance down and build a fresh one, which is the only
   * recovery that reaches state the instance owns privately: destroy() runs
   * KeyLoader.detach(), and that is what discards a cached `keyInfo` whose
   * key-load promise will never settle (`isCommonEncryption` entries go even
   * when their session context is null — the wedged shape). Equivalent to the
   * page reload viewers resort to, minus the page.
   *
   * Deliberately not a stopLoad()/startLoad(): those keep the media element
   * attached and re-await the same cached promise.
   *
   * Callers own their own budget — this is the mechanism, not the policy.
   */
  private rebuildInstance(reason: string): void {
    if (!this.hls) return;
    try {
      this.hls.destroy();
    } catch (e) {
      console.warn(`havik-player: hls.js destroy() threw during ${reason}`, e);
    }
    this.hls = null;
    this.startHls();
  }

  private onError(data: ErrorData): void {
    // Counted BEFORE the non-fatal early return: hls.js retries a failed
    // segment internally, so every request error a viewer actually suffers
    // is invisible to the QoE plane unless it is tallied here.
    //
    // Fragment failures only. requestCount counts fragment loads, so
    // folding manifest and playlist failures in here would let a
    // manifest-flapping session report an error ratio above 100% against a
    // denominator those errors were never part of.
    if (
      data.type === Hls.ErrorTypes.NETWORK_ERROR &&
      String(data.details ?? '').startsWith('frag')
    ) {
      this.requestErrors += 1;
    }
    if (!data.fatal) return; // hls.js recovers non-fatal gaps internally
    const hls = this.hls;
    if (!hls) return;

    // A fatal error cancels any pending recovery-budget refund so a tight
    // recover-loop still trips the caps.
    this.clearRecoveryReset();

    // A transient transport failure of the license/cert request is recoverable
    // by rebuilding hls.js; a real auth/entitlement denial and other key-system
    // errors are not.
    if (data.type === Hls.ErrorTypes.KEY_SYSTEM_ERROR) {
      if (this.scheduleLicenseRetry(data)) return;
      this.deps.emit({
        type: 'error',
        error: this.toError(data, 'DRM'),
        fatal: true,
        detail: data.details,
      });
      return;
    }
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
      // Transient origin/segment/license network failure: restart the loader with
      // exponential backoff, but cap retries so a persistently-failing origin
      // eventually surfaces a fatal error instead of restarting forever.
      if (this.networkRecoveries < MAX_NETWORK_RECOVERIES) {
        this.networkRecoveries += 1;
        const delay = Math.min(8000, 1000 * 2 ** (this.networkRecoveries - 1));
        this.networkRetryTimer = setTimeout(() => {
          this.networkRetryTimer = null;
          if (this.hls) this.hls.startLoad();
        }, delay);
        return;
      }
      this.deps.emit({
        type: 'error',
        error: this.toError(data, 'NETWORK'),
        fatal: true,
        detail: data.details,
      });
      return;
    }
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
      if (this.mediaRecoveries < MAX_MEDIA_RECOVERIES) {
        this.mediaRecoveries += 1;
        hls.recoverMediaError();
        return;
      }
      this.deps.emit({
        type: 'error',
        error: this.toError(data),
        fatal: true,
        detail: data.details,
      });
      return;
    }
    this.deps.emit({ type: 'error', error: this.toError(data), fatal: true, detail: data.details });
  }

  private toError(data: ErrorData, kind = 'PLAYBACK'): PlaybackError {
    const status = data.response?.code ?? 0;
    const detail = data.details ?? data.type;
    const reason = data.reason ?? data.error?.message ?? '';
    const code =
      kind === 'DRM'
        ? classifyDrmStatus(status)
        : status === 401
          ? 'UNAUTHORIZED'
          : status === 403
            ? 'FORBIDDEN'
            : 'INTERNAL';
    return new PlaybackError(
      code,
      status,
      `${kind} error: ${detail}${reason ? ` (${reason})` : ''}`,
    );
  }
}
